import type {
  RubricCriterion,
  RubricCriterionCount,
  RubricDraft,
  RubricLevel,
  RubricLevelCount,
  RubricPerRoleCriterionCount,
  RubricProjectRole,
  RubricTransversalCriterionCount,
} from '../types/rubricDraft';
import {
  RUBRIC_CRITERION_COUNT_MAX,
  RUBRIC_CRITERION_COUNT_MIN,
  RUBRIC_ROLE_ALL,
  normalizeApplicableRoleIds,
  normalizeCriterionOaIds,
  resolveRubricOas,
  rubricEvaluationTypeLabel,
} from '../types/rubricDraft';

const LEVEL_PRESETS: Record<RubricLevelCount, Array<{ name: string; points: number }>> = {
  3: [
    { name: 'Logrado', points: 6 },
    { name: 'En vías de logro', points: 4 },
    { name: 'Por lograr', points: 2 },
  ],
  4: [
    { name: 'Destacado', points: 7 },
    { name: 'Logrado', points: 5 },
    { name: 'En vías de logro', points: 3 },
    { name: 'Por lograr', points: 1 },
  ],
  5: [
    { name: 'Excelente', points: 8 },
    { name: 'Destacado', points: 6 },
    { name: 'Logrado', points: 4 },
    { name: 'En vías de logro', points: 2 },
    { name: 'Por lograr', points: 1 },
  ],
};

export const RUBRIC_LEVEL_COUNT_OPTIONS: Array<{ count: RubricLevelCount; label: string }> = [
  { count: 3, label: '3 niveles' },
  { count: 4, label: '4 niveles' },
  { count: 5, label: '5 niveles' },
];

export const RUBRIC_CRITERION_COUNT_OPTIONS: Array<{ count: RubricCriterionCount; label: string }> =
  Array.from(
    { length: RUBRIC_CRITERION_COUNT_MAX - RUBRIC_CRITERION_COUNT_MIN + 1 },
    (_, index) => {
      const count = RUBRIC_CRITERION_COUNT_MIN + index;
      return { count, label: String(count) };
    },
  );

export const RUBRIC_TRANSVERSAL_COUNT_OPTIONS: Array<{
  count: RubricTransversalCriterionCount;
  label: string;
}> = [
  { count: 0, label: '0' },
  { count: 1, label: '1' },
  { count: 2, label: '2' },
  { count: 3, label: '3' },
  { count: 4, label: '4' },
];

export const RUBRIC_PER_ROLE_COUNT_OPTIONS: Array<{
  count: RubricPerRoleCriterionCount;
  label: string;
}> = [
  { count: 1, label: '1' },
  { count: 2, label: '2' },
  { count: 3, label: '3' },
  { count: 4, label: '4' },
  { count: 5, label: '5' },
];

export function buildRubricLevels(count: RubricLevelCount): RubricLevel[] {
  return LEVEL_PRESETS[count].map((level, index) => ({
    id: `nivel-${index + 1}`,
    name: level.name,
    points: level.points,
  }));
}

export function createEmptyCriterion(index: number, levels: RubricLevel[]): RubricCriterion {
  const cells: Record<string, string> = {};
  for (const level of levels) {
    cells[level.id] = '';
  }
  return {
    id: `criterio-${index + 1}`,
    name: `Criterio ${index + 1}`,
    description: '',
    cells,
    applicableRoleIds: [RUBRIC_ROLE_ALL],
    weight: 1,
    oaIds: [],
  };
}

export function createEmptyRubricDraft(id: string, courseId: string): RubricDraft {
  const now = Date.now();
  const levels = buildRubricLevels(3);
  return {
    id,
    courseId,
    title: '',
    objective: '',
    evaluationObjective: '',
    evaluationType: 'formativa',
    curriculumMode: 'mono',
    extraSubjectTags: [],
    targetLevels: [],
    scope: 'individual',
    criterionCount: 4,
    transversalCriterionCount: 2,
    perRoleCriterionCount: 2,
    estadoProyecto: 'inicio',
    avanceDescripcion: '',
    projectRoles: [],
    levelCount: 3,
    levels,
    criteria: [],
    hasMatrix: false,
    createdAt: now,
    updatedAt: now,
  };
}

export interface AiRubricCriterion {
  name: string;
  description?: string;
  /** Nombres o ids de roles; "*" / "todos" = transversal. */
  applicableRoles?: string[];
  roleIds?: string[];
  /** Códigos OA (metadato); no van en la prosa. */
  oaIds?: string[];
  levels?: Array<{ description?: string; descripcion?: string }>;
}

export interface AiRubricResponse {
  title?: string;
  /** Objetivo imprimible (no el brief docente). */
  evaluationObjective?: string;
  /** @deprecated Preferir evaluationObjective. */
  objective?: string;
  criteria?: AiRubricCriterion[];
}

function resolveAiRoleIds(
  row: AiRubricCriterion,
  projectRoles: RubricProjectRole[],
  isGroup: boolean,
): string[] {
  if (!isGroup || !projectRoles.length) return [RUBRIC_ROLE_ALL];

  const raw = [
    ...(Array.isArray(row.roleIds) ? row.roleIds : []),
    ...(Array.isArray(row.applicableRoles) ? row.applicableRoles : []),
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  if (!raw.length) return [RUBRIC_ROLE_ALL];

  const expandToken = (token: string): string[] => {
    const lower = token.toLowerCase();
    if (token === RUBRIC_ROLE_ALL || lower === 'todos' || lower === 'todos los roles' || lower === 'all') {
      return [RUBRIC_ROLE_ALL];
    }

    const byId = projectRoles.find((role) => role.id === token);
    if (byId) return [byId.id];

    const byName = projectRoles.find((role) => role.name.trim().toLowerCase() === lower);
    if (byName) {
      // Si el "rol" es un compuesto inventado que también está en projectRoles,
      // preferimos mapear a roles reales separados cuando existan.
      if (/\s+y\s+|\s+e\s+|\/|,|;|&/i.test(token)) {
        const parts = token
          .split(/\s+y\s+|\s+e\s+|\/|,|;|&/i)
          .map((part) => part.trim())
          .filter(Boolean);
        const mapped: string[] = [];
        for (const part of parts) {
          const partLower = part.toLowerCase();
          const hit = projectRoles.find(
            (role) =>
              role.id !== byName.id &&
              (role.name.trim().toLowerCase() === partLower ||
                (role.name.trim().length >= 3 &&
                  (partLower.includes(role.name.trim().toLowerCase()) ||
                    role.name.trim().toLowerCase().includes(partLower)))),
          );
          if (hit) mapped.push(hit.id);
        }
        if (mapped.length >= 2) return [...new Set(mapped)];
      }
      return [byName.id];
    }

    // "Sonidista y sección trivia" / "Locución, Curación" → intentar mapear a roles reales
    const parts = token
      .split(/\s+y\s+|\s+e\s+|\/|,|;|&/i)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length > 1) {
      const mapped: string[] = [];
      for (const part of parts) {
        const ids = expandToken(part);
        for (const id of ids) {
          if (id !== RUBRIC_ROLE_ALL) mapped.push(id);
        }
      }
      if (mapped.length) return mapped;
    }

    // Contención: el token menciona el nombre de un rol conocido
    const contained = projectRoles.filter((role) => {
      const roleName = role.name.trim().toLowerCase();
      return roleName.length >= 3 && (lower.includes(roleName) || roleName.includes(lower));
    });
    if (contained.length) return contained.map((role) => role.id);

    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        `[rubric] Rol IA no reconocido (descartado): "${token}". Roles válidos: ${projectRoles
          .map((r) => r.name)
          .join(', ')}`,
      );
    }
    return [];
  };

  const ids: string[] = [];
  for (const token of raw) {
    const expanded = expandToken(token);
    if (expanded.includes(RUBRIC_ROLE_ALL)) return [RUBRIC_ROLE_ALL];
    ids.push(...expanded);
  }

  return normalizeApplicableRoleIds(ids.length ? ids : [RUBRIC_ROLE_ALL]);
}

function resolveAiOaIds(
  row: AiRubricCriterion,
  allowedCodes: string[],
): string[] {
  const raw = Array.isArray(row.oaIds)
    ? row.oaIds.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  if (!raw.length) return [];
  if (!allowedCodes.length) return normalizeCriterionOaIds(raw);

  const allowed = new Set(allowedCodes.map((code) => code.toLowerCase()));
  return normalizeCriterionOaIds(
    raw.filter((code) => {
      const lower = code.toLowerCase();
      if (allowed.has(lower)) return true;
      // Acepta "OA 01" vs "OA01"
      const compact = lower.replace(/\s+/g, '');
      return [...allowed].some((item) => item.replace(/\s+/g, '') === compact);
    }),
  );
}

/** Fusiona la respuesta IA en un borrador existente. */
export function applyAiRubricToDraft(
  draft: RubricDraft,
  ai: AiRubricResponse,
): RubricDraft {
  const isGroup = draft.scope === 'group';
  const projectRoles = draft.projectRoles ?? [];
  const allowedOaCodes = (draft.oas ?? [])
    .map((oa) => oa.code)
    .filter(Boolean);
  const criteria: RubricCriterion[] = [];
  let nextIndex = 0;

  for (const row of ai.criteria ?? []) {
    const cells: Record<string, string> = {};
    draft.levels.forEach((level, levelIndex) => {
      const fromRow = row.levels?.[levelIndex];
      const text = fromRow?.description ?? fromRow?.descripcion ?? '';
      cells[level.id] = String(text || '').trim();
    });

    const baseName = String(row.name || '').trim();
    const description = String(row.description || '').trim();
    const roleIds = resolveAiRoleIds(row, projectRoles, isGroup);
    const oaIds = resolveAiOaIds(row, allowedOaCodes);

    nextIndex += 1;
    criteria.push({
      id: `criterio-${nextIndex}`,
      name: baseName || `Criterio ${nextIndex}`,
      description,
      cells,
      applicableRoleIds: roleIds,
      weight: 1,
      oaIds,
    });
  }

  const aiTitle = String(ai.title || '').trim();
  const aiEvaluationObjective = String(
    ai.evaluationObjective || ai.objective || '',
  ).trim();
  const hasOas = resolveRubricOas(draft).length > 0;
  // Sin OA: preferir título redactado por la IA (el draft suele traer el brief truncado).
  const nextTitle = hasOas
    ? draft.title.trim() || aiTitle || `Rúbrica de ${rubricEvaluationTypeLabel(draft.evaluationType).toLowerCase()}`
    : aiTitle ||
      draft.title.trim() ||
      `Rúbrica de ${rubricEvaluationTypeLabel(draft.evaluationType).toLowerCase()}`;

  return {
    ...draft,
    title: nextTitle,
    evaluationObjective: aiEvaluationObjective || draft.evaluationObjective || '',
    projectRoles,
    criteria: criteria.length > 0 ? criteria : draft.criteria,
    hasMatrix: criteria.length > 0,
    updatedAt: Date.now(),
  };
}

/**
 * Reemplaza un criterio existente con el generado por IA (misma id).
 * No conserva nombre, descripción ni descriptores previos: la fila queda
 * alineada solo al resultado de la instrucción enviada a la IA.
 */
export function applyAiCriterionToDraft(
  draft: RubricDraft,
  criterionId: string,
  ai: AiRubricCriterion,
): RubricDraft {
  const isGroup = draft.scope === 'group';
  const projectRoles = draft.projectRoles ?? [];
  const allowedOaCodes = (draft.oas ?? []).map((oa) => oa.code).filter(Boolean);
  const criteria = draft.criteria.map((criterion) => {
    if (criterion.id !== criterionId) return criterion;
    const cells: Record<string, string> = {};
    draft.levels.forEach((level, levelIndex) => {
      const fromRow = ai.levels?.[levelIndex];
      const text = fromRow?.description ?? fromRow?.descripcion ?? '';
      cells[level.id] = String(text || '').trim();
    });
    const name = String(ai.name || '').trim() || 'Criterio';
    const description = String(ai.description || '').trim();
    const nextRoles =
      ai.applicableRoles || ai.roleIds
        ? resolveAiRoleIds(ai, projectRoles, isGroup)
        : [RUBRIC_ROLE_ALL];
    const oaIds =
      Array.isArray(ai.oaIds) && ai.oaIds.length
        ? resolveAiOaIds(ai, allowedOaCodes)
        : [];
    return {
      ...criterion,
      name,
      description,
      cells,
      applicableRoleIds: isGroup ? nextRoles : [RUBRIC_ROLE_ALL],
      oaIds,
    };
  });

  return {
    ...draft,
    criteria,
    hasMatrix: criteria.length > 0,
    updatedAt: Date.now(),
  };
}
